import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { Warehouse, Package, DollarSign, TrendingUp, Plus, Truck, Forklift, Users } from 'lucide-react';

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    warehouses: 0,
    mhe: 0,
    fleet: 0,
    manpower: 0,
    bookings: 0,
    activeBookings: 0,
    totalRevenue: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  // Check if provider offers specific services
  const serviceTypes = user?.service_types || ['warehousing', 'fleet', 'mhe', 'manpower'];
  const offersWarehousing = serviceTypes.includes('warehousing');
  const offersFleet = serviceTypes.includes('fleet');
  const offersMhe = serviceTypes.includes('mhe');
  const offersManpower = serviceTypes.includes('manpower');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [warehousesRes, mheRes, fleetRes, manpowerRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/provider/warehouses`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/provider/mhe`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/provider/fleet`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/provider/manpower`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/bookings`).catch(() => ({ data: [] }))
      ]);
      
      const bookings = bookingsRes.data || [];
      const totalRevenue = bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
      const activeBookings = bookings.filter(b => b.status === 'active' || b.status === 'pending').length;
      
      setStats({
        warehouses: (warehousesRes.data || []).length,
        mhe: (mheRes.data || []).length,
        fleet: (fleetRes.data || []).length,
        manpower: (manpowerRes.data || []).length,
        bookings: bookings.length,
        activeBookings,
        totalRevenue
      });
      setRecentBookings(bookings.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
            Service Provider Dashboard
          </h1>
          <p className="text-muted-foreground">Manage your services and bookings</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-8">
          {offersWarehousing && (
            <Button
              onClick={() => navigate('/provider/add-warehouse')}
              data-testid="add-warehouse-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm transition-all active:scale-95"
            >
              <Warehouse className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Add Warehouse
            </Button>
          )}
          {offersMhe && (
            <Button
              onClick={() => navigate('/provider/add-mhe')}
              data-testid="add-mhe-btn"
              className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm transition-all active:scale-95"
            >
              <Forklift className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Add MHE
            </Button>
          )}
          {offersFleet && (
            <Button
              onClick={() => navigate('/provider/add-fleet')}
              data-testid="add-fleet-btn"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm transition-all active:scale-95"
            >
              <Truck className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Add Fleet
            </Button>
          )}
          {offersManpower && (
            <Button
              onClick={() => navigate('/provider/add-manpower')}
              data-testid="add-manpower-btn"
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm transition-all active:scale-95"
            >
              <Users className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Add Manpower
            </Button>
          )}
        </div>

        {/* Service Types Badge */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">Your Services:</p>
          <div className="flex flex-wrap gap-2">
            {offersWarehousing && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                <Warehouse className="h-4 w-4" /> Warehousing
              </span>
            )}
            {offersFleet && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                <Truck className="h-4 w-4" /> Fleet
              </span>
            )}
            {offersMhe && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                <Forklift className="h-4 w-4" /> MHE
              </span>
            )}
            {offersManpower && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                <Users className="h-4 w-4" /> Manpower
              </span>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8">
          {offersWarehousing && (
            <Card className="bg-white border border-border rounded-sm shadow-sm px-3 py-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Warehouses</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xl font-bold text-foreground">{stats.warehouses}</p>
                <Warehouse className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />
              </div>
            </Card>
          )}
          
          {offersMhe && (
            <Card className="bg-white border border-border rounded-sm shadow-sm px-3 py-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">MHE</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xl font-bold text-foreground">{stats.mhe}</p>
                <Forklift className="h-3.5 w-3.5 text-amber-600" strokeWidth={1.5} />
              </div>
            </Card>
          )}
          
          {offersFleet && (
            <Card className="bg-white border border-border rounded-sm shadow-sm px-3 py-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Fleet</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xl font-bold text-foreground">{stats.fleet}</p>
                <Truck className="h-3.5 w-3.5 text-blue-600" strokeWidth={1.5} />
              </div>
            </Card>
          )}
          
          {offersManpower && (
            <Card className="bg-white border border-border rounded-sm shadow-sm px-3 py-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Manpower</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xl font-bold text-foreground">{stats.manpower}</p>
                <Users className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.5} />
              </div>
            </Card>
          )}

          <Card className="bg-white border border-border rounded-sm shadow-sm px-3 py-3">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Bookings</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xl font-bold text-foreground">{stats.bookings}</p>
              <Package className="h-3.5 w-3.5 text-purple-600" strokeWidth={1.5} />
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm px-3 py-3">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Revenue</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xl font-bold text-foreground">${stats.totalRevenue.toFixed(0)}</p>
              <DollarSign className="h-3.5 w-3.5 text-green-600" strokeWidth={1.5} />
            </div>
          </Card>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">Manage Services</h2>
            <div className="grid grid-cols-2 gap-3">
              {offersWarehousing && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/provider/warehouses')}
                  className="h-auto py-4 flex flex-col items-center gap-2 rounded-sm"
                >
                  <Warehouse className="h-6 w-6 text-primary" />
                  <span>My Warehouses</span>
                </Button>
              )}
              {offersMhe && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/provider/mhe')}
                  className="h-auto py-4 flex flex-col items-center gap-2 rounded-sm"
                >
                  <Forklift className="h-6 w-6 text-amber-600" />
                  <span>My MHE</span>
                </Button>
              )}
              {offersFleet && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/provider/fleet')}
                  className="h-auto py-4 flex flex-col items-center gap-2 rounded-sm"
                >
                  <Truck className="h-6 w-6 text-blue-600" />
                  <span>My Fleet</span>
                </Button>
              )}
              {offersManpower && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/provider/manpower')}
                  className="h-auto py-4 flex flex-col items-center gap-2 rounded-sm"
                >
                  <Users className="h-6 w-6 text-emerald-600" />
                  <span>My Manpower</span>
                </Button>
              )}
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">Recent Bookings</h2>
            {recentBookings.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No bookings yet</p>
            ) : (
              <div className="space-y-3">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between p-3 border border-border rounded-sm"
                  >
                    <div>
                      <p className="font-medium text-foreground text-sm">Booking #{booking.id.slice(0, 8)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(booking.booking_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-foreground text-sm">${booking.total_amount?.toFixed(2) || '0.00'}</p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${
                        booking.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        booking.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProviderDashboard;
